"use client";

import React, { createContext, useContext, useMemo, useState } from "react";
import type { MindstringPrototype, Situation } from "@/generated/fastapi";
import useConfirmationDialog from "@/shared/ui/useConfirmationDialog";
import type { ConfirmationDialogController } from "@/shared/ui/useConfirmationDialog";
import type { AssignSituationStrategy } from "@/generated/fastapi";

interface SuggestedThoughtConfirmationValue {
  reassignSingleThoughtToSituation: Situation[];
  setReassignSingleThoughtToSituation: React.Dispatch<
    React.SetStateAction<Situation[]>
  >;
  confirmationPrototypes: MindstringPrototype[];
  setConfirmationPrototypes: React.Dispatch<
    React.SetStateAction<MindstringPrototype[]>
  >;
  confirmSingleThoughtConfirmation: ConfirmationDialogController<{
    situationId?: string;
    assignStrategy?: AssignSituationStrategy;
  }>;
}

const SuggestedThoughtConfirmationContext = createContext<
  SuggestedThoughtConfirmationValue | undefined
>(undefined);

export function SuggestedThoughtConfirmationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [
    reassignSingleThoughtToSituation,
    setReassignSingleThoughtToSituation,
  ] = useState<Situation[]>([]);
  const [confirmationPrototypes, setConfirmationPrototypes] = useState<
    MindstringPrototype[]
  >([]);
  const confirmSingleThoughtConfirmation = useConfirmationDialog<{
    situationId?: string;
    assignStrategy?: AssignSituationStrategy;
  }>();

  const contextValue = useMemo(
    () => ({
      reassignSingleThoughtToSituation,
      setReassignSingleThoughtToSituation,
      confirmationPrototypes,
      setConfirmationPrototypes,
      confirmSingleThoughtConfirmation,
    }),
    [
      reassignSingleThoughtToSituation,
      confirmationPrototypes,
      confirmSingleThoughtConfirmation,
      setReassignSingleThoughtToSituation,
      setConfirmationPrototypes,
    ],
  );

  return (
    <SuggestedThoughtConfirmationContext.Provider value={contextValue}>
      {children}
    </SuggestedThoughtConfirmationContext.Provider>
  );
}

export function useSuggestedThoughtConfirmationContext() {
  const context = useContext(SuggestedThoughtConfirmationContext);

  if (context === undefined) {
    throw new Error(
      "useSuggestedThoughtConfirmationContext must be used within SuggestedThoughtConfirmationProvider",
    );
  }

  return context;
}
